/** A screen Wake Lock sentinel as far as this controller cares: something releasable. */
export interface ReleasableLock {
  release(): Promise<void> | void;
}

/** Decides when to hold a screen Wake Lock: only while `wanted` holds — i.e. the user
 *  hasn't opted out (settings.keepAwake) AND the Now tab is actively following the live
 *  position in the foreground. A sleeping device cannot surface an alert (the web-app
 *  limitation the You-tab warning calls out), so the lock keeps the display awake.
 *
 *  The platform auto-releases the lock when the tab is hidden; the caller re-runs
 *  update() on visibilitychange, so a still-wanted lock is re-acquired on return.
 *  Pure of the Wake Lock API — the caller supplies acquire() (navigator.wakeLock.request)
 *  and the returned sentinel exposes release(). Acquire may reject (e.g. not visible) or
 *  return null (unsupported); both resolve to "not held". */
export class WakeLockController {
  #lock: ReleasableLock | null = null;
  #acquiring = false;
  #wanted = false;
  constructor(private deps: { acquire: () => Promise<ReleasableLock | null> }) {}

  async update(s: { wanted: boolean }): Promise<void> {
    this.#wanted = s.wanted;
    if (s.wanted) {
      if (this.#lock || this.#acquiring) return; // already held / request in flight
      this.#acquiring = true;
      try {
        const lock = await this.deps.acquire();
        // If `wanted` flipped to false while the request was in flight, don't keep it.
        if (this.#wanted && lock) this.#lock = lock;
        else await lock?.release();
      } catch {
        this.#lock = null; // request rejected (e.g. document not visible)
      } finally {
        this.#acquiring = false;
      }
    } else {
      const lock = this.#lock;
      this.#lock = null;
      try {
        await lock?.release();
      } catch {
        /* already auto-released by the platform (tab hidden) */
      }
    }
  }

  get held(): boolean {
    return this.#lock !== null;
  }

  /** Release and stop wanting the lock (identity erase / teardown). */
  stop(): void {
    void this.update({ wanted: false });
  }
}
